import React, { useEffect, useRef, useState } from "react";
import { ChevronRight, Check } from "lucide-react";
import { pricing } from "../mock";

const PricingSection = () => {
  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [billing, setBilling] = useState("monthly");
  const billingLabels = {
    monthly: "Monatlich",
    yearly: "Jährlich",
  };

  useEffect(() => {
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const el = sectionRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight;
        // 0 -> section entering from bottom (cards far below title)
        // 1 -> section scrolled up (cards risen close to title)
        const raw = (vh * 0.9 - rect.top) / (vh * 0.7);
        setProgress(Math.min(1, Math.max(0, raw)));
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <section
      id="pricing"
      ref={sectionRef}
      className="relative overflow-hidden bg-[#EDF1F8] pb-40 pt-20"
    >
      {/* title + toggle */}
      <div className="mx-auto max-w-[1180px] px-6 text-center">
        <h2 className="font-serif text-[#16263F] text-[10vw] leading-[1] tracking-tight md:text-[4.4rem]">
          {pricing.title}
        </h2>

        <div className="mt-12 inline-flex items-center gap-1 rounded-full border border-white/70 bg-white/50 p-1.5 shadow-[0_8px_24px_rgba(20,40,70,0.06)] backdrop-blur">
          {["monthly", "yearly"].map((b) => (
            <button
              key={b}
              onClick={() => setBilling(b)}
              className={`rounded-full px-7 py-2.5 text-[15px] font-semibold transition-all duration-200 ${
                billing === b
                  ? "bg-[#5BA4FB] text-white shadow-[0_6px_16px_rgba(91,164,251,0.4)]"
                  : "text-slate-500 hover:text-[#16263F]"
              }`}
            >
              {billingLabels[b]}
            </button>
          ))}
        </div>
      </div>

      {/* cards rise toward the title on scroll */}
      <div className="mx-auto mt-20 grid max-w-[1180px] grid-cols-1 gap-7 px-6 md:grid-cols-3">
        {pricing.plans.map((plan, i) => {
          const base = 150 + Math.abs(i - 1) * 45; // outer cards start lower
          const translateY = (1 - progress) * base;
          return (
            <div
              key={plan.name}
              className={`flex flex-col rounded-[28px] border border-dashed p-4 ${
                plan.popular
                  ? "border-[#5BA4FB]/50"
                  : "border-slate-300"
              }`}
              style={{
                transform: `translateY(${translateY}px)`,
                opacity: 0.25 + progress * 0.75,
                transition: "transform 0.1s linear, opacity 0.1s linear",
              }}
            >
              {/* price card */}
              <div
                className={`rounded-[22px] p-7 ${
                  plan.popular
                    ? "bg-white shadow-[0_18px_50px_rgba(20,40,70,0.10)]"
                    : "bg-[#E5EBF4]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-lg font-medium text-[#16263F]">
                    {plan.name}
                  </span>
                  {plan.popular && (
                    <span className="rounded-full bg-[#5BA4FB]/15 px-3 py-1 text-xs font-semibold text-[#2F80E8]">
                      Empfohlen
                    </span>
                  )}
                </div>
                <div className="mt-6 flex items-end">
                  <span className="text-6xl font-bold tracking-tight text-[#16263F]">
                    {billing === "monthly" ? plan.monthly : plan.yearly}
                  </span>
                  {plan.suffix && (
                    <span className="mb-2 ml-1 text-base font-medium text-slate-400">
                      {plan.suffix}
                    </span>
                  )}
                </div>
              </div>

              <p className="px-2 pt-8 text-[15px] text-slate-400">
                {plan.description}
              </p>

              <ul className="flex-1 space-y-5 px-2 pt-8">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-3">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#5BA4FB]/15">
                      <Check className="h-3.5 w-3.5 text-[#5BA4FB]" strokeWidth={3} />
                    </span>
                    <span className="text-[16px] font-medium text-[#2a3f5f]">{f}</span>
                  </li>
                ))}
              </ul>

              <button
                className={`mt-10 flex w-full items-center justify-center gap-1.5 rounded-2xl py-4 text-[16px] font-semibold transition-all duration-200 hover:-translate-y-0.5 ${
                  plan.popular
                    ? "bg-[#5BA4FB] text-white shadow-[0_12px_30px_rgba(91,164,251,0.4)] hover:bg-[#7AB5FC]"
                    : "bg-[#16263F] text-white hover:bg-[#223a5e]"
                }`}
              >
                Demo anfragen <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PricingSection;
